import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "./components/theme-provider";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Leads from "./pages/Leads";
import Projects from "./pages/Projects";
import Production from "./pages/Production";
import Customers from "./pages/Customers";
import Estimates from "./pages/Estimates";
import EstimateCreate from "./pages/EstimateCreate";
import Invoices from "./pages/Invoices";
import InvoiceCreate from "./pages/InvoiceCreate";
import InvoiceDetail from "./pages/InvoiceDetail";
import Schedule from "./pages/Schedule";
import Callbacks from "./pages/Callbacks";
import Reports from "./pages/ReportsNew";
import Settings from "./pages/Settings";
import Subcontractors from "./pages/Subcontractors";
import Designers from "./pages/Designers";
import Vendors from "./pages/Vendors";
import Automations from "./pages/Automations";
import EmailTemplates from "./pages/EmailTemplates";
import SendEmail from "./pages/SendEmail";
import Tasks from "./pages/Tasks";
import Layout from "./components/Layout";

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="reliable-crm-theme">
        <div className="App">
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                
                {/* Protected routes */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/leads" element={<Leads />} />
                          <Route path="/projects" element={<Projects />} />
                          <Route path="/production" element={<Production />} />
                          <Route path="/customers" element={<Customers />} />
                          <Route path="/subcontractors" element={<Subcontractors />} />
                          <Route path="/designers" element={<Designers />} />
                          <Route path="/vendors" element={<Vendors />} />
                          <Route path="/estimates" element={<Estimates />} />
                          <Route path="/estimates/create" element={<EstimateCreate />} />
                          <Route path="/estimates/:estimateId/edit" element={<EstimateCreate />} />
                          <Route path="/work-items" element={<Navigate to="/settings?tab=work-items" replace />} />
                          <Route path="/invoices" element={<Invoices />} />
                          <Route path="/invoices/create" element={<InvoiceCreate />} />
                          <Route path="/invoices/:invoiceId" element={<InvoiceDetail />} />
                          <Route path="/invoices/:invoiceId/edit" element={<InvoiceCreate />} />
                          <Route path="/schedule" element={<Schedule />} />
                          <Route path="/callbacks" element={<Callbacks />} />
                          <Route 
                            path="/automations" 
                            element={
                              <ProtectedRoute requireAdmin={true}>
                                <Automations />
                              </ProtectedRoute>
                            } 
                          />
                          <Route 
                            path="/email-templates" 
                            element={
                              <ProtectedRoute requireAdmin={true}>
                                <EmailTemplates />
                              </ProtectedRoute>
                            } 
                          />
                          <Route path="/send-email" element={<SendEmail />} />
                          <Route path="/tasks" element={<Tasks />} />
                          <Route path="/reports" element={<Reports />} />
                          <Route path="/settings" element={<Settings />} />
                        </Routes>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
          <Toaster />
        </div>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;